import React, { useState } from "react";
import {
  FormGroup,
  FormControl,
  Button,
  FloatingLabel,
  FormSelect,
  FormCheck,
  Card,
  Row,
  Col,
  Badge,
} from "react-bootstrap";
import { toast } from "react-toastify";
import InputMask from "react-input-mask";
import { Helmet } from "react-helmet-async";
import AddCircle from "@mui/icons-material/AddCircle";
import Edit from "@mui/icons-material/Edit";
import Delete from "@mui/icons-material/Delete";

import { useUser } from "../../../contexts/UserContext";
import SubInternalLayout from "../../layout/SubInternalLayout";
import { useFormData } from "../../../hooks/useFormData";
import { useApiPut } from "../../../hooks/useApi";
import SpinIcon from "../../common/SpinIcon";
import Prompt from "../../common/Prompt";

const defaultVals = {
  name: "",
  phone: "",
  relationship: "Child",
  relationshipOther: null,
  primaryContact: false,
};

const EmergencyContacts = () => {
  const { user, updateUser } = useUser();
  const [edit, setEdit] = useState(null);
  const { formData, hasChanged, onChange, setData, setHasChanged } =
    useFormData(defaultVals);

  const save = useApiPut("/users/profile", (res) => {
    toast.success("Your changes have been successfully saved.");
    updateUser(res);
    setEdit(null);
    setHasChanged(false);
  });

  const onSubmit = (e) => {
    e.preventDefault();

    const existing = user.helper.emergencyContacts || [];
    const contacts = [...existing];

    if (edit === "new") {
      contacts.push({ ...formData });
    } else {
      // do the whole for each stuff again since we are spreading and will lose the reference to the key
      contacts.forEach((con, conIndex) => {
        if (con._id === edit) {
          contacts[conIndex] = { ...con, ...formData };
        }
      });
    }

    const payload = {
      "helper.profile.emergencyContacts": contacts,
    };

    save.mutate(payload);
  };

  const showForm = () => {
    return (
      <Card style={{ maxWidth: 450 }}>
        <Card.Body>
          <form onSubmit={onSubmit}>
            <FormGroup className="mb-4">
              <FloatingLabel label="Full Name">
                <FormControl
                  type="text"
                  name="name"
                  placeholder="Full Name"
                  onChange={onChange}
                  value={formData.name}
                  required={true}
                />
              </FloatingLabel>
            </FormGroup>

            <FormGroup className="mb-4">
              <InputMask
                type="tel"
                name="phone"
                mask="999-999-9999"
                className="form-control"
                placeholder="123-123-1234"
                onChange={onChange}
                value={formData.phone}
                required={true}
              />
            </FormGroup>

            <FormGroup className="mb-4">
              <FormSelect
                size="lg"
                name="relationship"
                value={formData.relationship}
                required={true}
                onChange={onChange}
              >
                <option value="Child">Child</option>
                <option value="Spouse">Spouse</option>
                <option value="Parent">Parent</option>
                <option value="Niece/Nephew">Niece/Nephew</option>
                <option value="Other">Other</option>
              </FormSelect>
            </FormGroup>

            {formData.relationship === "Other" ? (
              <FormGroup className="mb-4">
                <FormControl
                  size="lg"
                  name="relationshipOther"
                  value={formData.relationshipOther || ""}
                  required={true}
                  onChange={onChange}
                  placeholder="Other Relationship"
                />
              </FormGroup>
            ) : null}

            <FormGroup>
              <FormCheck
                type="checkbox"
                label="Primary Contact"
                name="primaryContact"
                value={true}
                checked={formData.primaryContact}
                onChange={(e) =>
                  onChange({
                    target: { name: "primaryContact", value: e.target.checked },
                  })
                }
              />
            </FormGroup>

            <div className="mt-3">
              <Row>
                <Col>
                  <Button
                    variant="link"
                    onClick={() => setEdit(null)}
                    style={{
                      textDecoration: "none",
                      fontWeight: 700,
                      paddingLeft: 0,
                    }}
                  >
                    CANCEL
                  </Button>
                </Col>
                <Col className="text-end">
                  <Button
                    type="submit"
                    disabled={
                      !hasChanged ||
                      !/^\d{3}-\d{3}-\d{4}$/.test(formData.phone) ||
                      save.isLoading
                    }
                  >
                    {save.isLoading ? <SpinIcon /> : "SAVE TO LIST"}
                  </Button>
                </Col>
              </Row>
            </div>
          </form>
        </Card.Body>
      </Card>
    );
  };

  const showList = () => {
    const list = user.helper.profile.emergencyContacts || [];
    return (
      <div>
        {list.map((contact, i) => {
          return (
            <div
              style={{ borderBottom: "1px solid #EBE0DE" }}
              key={i}
              className="mb-4"
            >
              <div className="float-end">
                <Button
                  size="sm"
                  variant="link"
                  style={{ textDecoration: "none" }}
                  onClick={() => {
                    setData({ ...formData, ...contact });
                    setEdit(contact._id);
                  }}
                >
                  <Edit />
                </Button>
                <Button
                  size="sm"
                  variant="link"
                  style={{ textDecoration: "none" }}
                  onClick={() => {
                    if (
                      window.confirm(
                        "Are you sure you want to delete this contact?"
                      )
                    ) {
                      const payload = {
                        "customer.profiles": [...user.customer.profiles],
                      };

                      payload["customer.profiles"].forEach((pro, proIndex) => {
                        pro.emergencyContacts = pro.emergencyContacts.filter(
                          (c) => c._id !== contact._id
                        );
                        payload["customer.profiles"][proIndex] = pro;
                      });

                      save.mutate(payload);
                    }
                  }}
                >
                  <Delete />
                </Button>
              </div>
              <p className="body2 mb-1">
                {contact.name}
                {contact.primaryContact ? (
                  <Badge bg="secondary" className="ms-2">
                    PRIMARY
                  </Badge>
                ) : null}
              </p>
              <p className="text-muted body1 mb-1">{contact.phone}</p>
              <p className="text-muted body1 mb-1">{contact.relationship}</p>
            </div>
          );
        })}
        <Button
          variant="link"
          onClick={() => {
            setData(defaultVals);
            setEdit("new");
          }}
          style={{ textDecoration: "none" }}
          className="ps-0"
        >
          <AddCircle /> ADD AN EMERGENCY CONTACT
        </Button>
      </div>
    );
  };

  return (
    <SubInternalLayout title={`Emergency Contacts`}>
      <Helmet>
        <title>Emergency Contacts | Profile | Manana</title>
        <meta name="description" content="Manana Help" />
      </Helmet>
      <Prompt when={hasChanged} />
      {edit !== null ? showForm() : showList()}
      <br />
      <br />
    </SubInternalLayout>
  );
};

export default EmergencyContacts;
